import { useState } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";

const FORWARD_EMAIL = "GulliverTravelsPlans@gmail.com";

export default function ShareModal({ trip, onClose, user }) {
  const [email, setEmail] = useState("");
  const [members, setMembers] = useState(trip.members || []);
  const [toast, setToast] = useState("");
  const [trustedEmail, setTrustedEmail] = useState("");
  const [trustedEmails, setTrustedEmails] = useState(trip.trustedEmails || []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); };

  const handleInvite = async () => {
    if (!email || members.includes(email)) return;
    // Write both members (email) and a pendingMembers field so fixSharing
    // can reliably bridge to memberIds when the invitee signs in
    await updateDoc(doc(db, "trips", trip.id), {
      members: arrayUnion(email),
      pendingMembers: arrayUnion(email.toLowerCase()),
    });
    setMembers([...members, email]);

    // Send invitation email
    try {
      await fetch("/api/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: email,
          inviterName: user?.displayName || user?.email || "Your travel companion",
          tripName: trip.name,
          tripId: trip.id,
        }),
      });
      showToast("Invitation sent!");
    } catch {
      showToast("Added! (invitation email failed)");
    }

    setEmail("");
  };

  const handleAddTrustedEmail = async () => {
    if (!trustedEmail || trustedEmails.includes(trustedEmail)) return;
    await updateDoc(doc(db, "trips", trip.id), {
      trustedEmails: arrayUnion(trustedEmail),
    });
    setTrustedEmails([...trustedEmails, trustedEmail]);
    setTrustedEmail("");
    showToast("Email address added!");
  };

  const handleCopy = (text) => {
    navigator.clipboard?.writeText(text);
    showToast("Copied!");
  };

  const calendarUrl = `https://gullivertravels.app/api/calendar?tripId=${trip.id}`;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Share trip</div>
          <button className="modal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        {/* Invite companions */}
        <div className="form-group">
          <label className="form-label">Invite a travel companion</label>
          <div className="share-row">
            <input
              className="form-input"
              placeholder="friend@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              style={{ flex: 1 }}
            />
            <button className="seg-btn primary" onClick={handleInvite}>Invite</button>
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-light)", marginTop: 4 }}>
            They'll see this trip when they sign in with this email address.
          </div>
        </div>

        {/* Members list */}
        {members.length > 0 && (
          <>
            <div className="form-label" style={{ marginBottom: 8 }}>Travelers</div>
            <div className="member-list" style={{ marginBottom: 16 }}>
              {members.map((m, i) => (
                <div key={i} className="member-row">
                  <span style={{ fontSize: 13 }}>{m}</span>
                  {m === user?.email && <span className="member-you">you</span>}
                </div>
              ))}
            </div>
          </>
        )}

        {/* Trusted sender emails */}
        <div className="forward-section">
          <div className="form-label" style={{ marginBottom: 6 }}>Forward confirmations</div>
          <div style={{ fontSize: 12, color: "var(--ink-light)", marginBottom: 8, lineHeight: 1.5 }}>
            Forward any confirmation email to this address — Claude will add it to the right trip automatically.
          </div>
          <div className="forward-address" style={{ marginBottom: 10 }}>
            <div className="forward-address-text">{FORWARD_EMAIL}</div>
            <button className="copy-btn" onClick={() => handleCopy(FORWARD_EMAIL)}>Copy</button>
          </div>

          <div style={{ fontSize: 12, color: "var(--ink-light)", marginBottom: 6 }}>
            Add email addresses you forward from:
          </div>
          <div className="share-row" style={{ marginBottom: 8 }}>
            <input
              className="form-input"
              placeholder="your@email.com"
              value={trustedEmail}
              onChange={(e) => setTrustedEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddTrustedEmail()}
              style={{ flex: 1 }}
            />
            <button className="seg-btn primary" onClick={handleAddTrustedEmail}>Add</button>
          </div>
          {trustedEmails.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {trustedEmails.map((e, i) => (
                <div key={i} style={{ fontSize: 11, background: "var(--paper-warm)", border: "1px solid var(--border)", borderRadius: 4, padding: "3px 8px", color: "var(--ink-mid)" }}>
                  {e}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Calendar subscription */}
        <div className="forward-section">
          <div className="form-label" style={{ marginBottom: 6 }}>Calendar</div>
          <div style={{ fontSize: 12, color: "var(--ink-light)", marginBottom: 10, lineHeight: 1.5 }}>
            Subscribe once to get <strong>all your trips</strong> in your calendar automatically — including new ones as you add them.
          </div>

          {/* Master feed — subscribe once for all trips */}
          {user?.uid && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-mid)", marginBottom: 6 }}>
                All trips (recommended)
              </div>
              <div className="forward-address" style={{ marginBottom: 8 }}>
                <div className="forward-address-text" style={{ fontSize: 10 }}>
                  {`https://gullivertravels.app/api/calendar?userId=${user.uid}`}
                </div>
                <button className="copy-btn" onClick={() => handleCopy(`https://gullivertravels.app/api/calendar?userId=${user.uid}`)}>Copy</button>
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <a
                  href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(`webcal://gullivertravels.app/api/calendar?userId=${user.uid}`)}`}
                  target="_blank" rel="noreferrer"
                  className="copy-btn"
                  style={{ textDecoration: "none", textAlign: "center" }}
                >
                  + Google Calendar
                </a>
                <a
                  href={`webcal://gullivertravels.app/api/calendar?userId=${user.uid}`}
                  className="copy-btn"
                  style={{ textDecoration: "none", textAlign: "center" }}
                >
                  + Apple Calendar
                </a>
              </div>
            </>
          )}

          {/* This trip only */}
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-mid)", marginBottom: 6 }}>
            This trip only
          </div>
          <div className="forward-address" style={{ marginBottom: 8 }}>
            <div className="forward-address-text" style={{ fontSize: 11 }}>{calendarUrl}</div>
            <button className="copy-btn" onClick={() => handleCopy(calendarUrl)}>Copy</button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <a
              href={`webcal://calendar.google.com/calendar/r?cid=${encodeURIComponent(calendarUrl)}`}
              target="_blank" rel="noreferrer"
              className="copy-btn"
              style={{ textDecoration: "none", textAlign: "center" }}
            >
              + Google Calendar
            </a>
            <a
              href={`webcal://gullivertravels.app/api/calendar?tripId=${trip.id}`}
              className="copy-btn"
              style={{ textDecoration: "none", textAlign: "center" }}
            >
              + Apple Calendar
            </a>
          </div>
        </div>

        {toast && <div className="toast">{toast}</div>}
      </div>
    </div>
  );
}
